// Copyright 2026 Hardfin, Inc.

/**
 * Brand autocomplete component for searching and selecting companies.
 * Displays company logos using Logo.dev with loading states.
 */
import { BrandResult } from '@/brand/hooks/use-brand-search';
import { CompanyLogo } from '@/customer/components/company-logo';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Props for the BrandAutocomplete component.
 */
interface Props {
  /**
   * Available brand options to display in the dropdown.
   */
  options: BrandResult[];

  /**
   * The current input value (controlled).
   */
  inputValue: string;

  /**
   * Callback fired when the input value changes.
   */
  onInputChange: (
    event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => void;

  /**
   * Callback fired when a brand is selected or the input is cleared.
   */
  onChange: (
    event: React.SyntheticEvent,
    value: BrandResult | string | null
  ) => void;

  /**
   * Name attribute for the form input element.
   */
  name?: string;

  /**
   * Label text displayed above the input field.
   */
  label?: string;

  /**
   * Controls the error state of the component.
   */
  error?: boolean;

  /**
   * Text displayed below the input field.
   */
  helperText?: string;

  /**
   * Placeholder text shown in the empty input field.
   */
  placeholder?: string;

  /**
   * Whether the autocomplete is currently searching.
   */
  isSearching?: boolean;

  /**
   * If true, the input field is disabled.
   */
  disabled?: boolean;
}

/**
 * Renders a single brand option in the dropdown list.
 */
function renderOption(
  attributes: HTMLAttributes<HTMLLIElement>,
  option: BrandResult
) {
  // Remove key from attributes since we set it explicitly
  const props = omit(attributes, 'key');

  return (
    <li {...props} key={option.domain}>
      <Stack direction="row" alignItems="center" spacing={1.5} width="100%">
        <CompanyLogo
          domain={option.domain}
          name={option.name}
          size="small"
          blurhash={option.blurhash}
        />
        <Stack direction="column" spacing={0} overflow="hidden">
          <Typography variant="body2" noWrap>
            {option.name}
          </Typography>
          <Typography variant="caption" noWrap>
            {option.domain}
          </Typography>
        </Stack>
      </Stack>
    </li>
  );
}

/**
 * Gets the display label for a brand option.
 */
function getOptionLabel(option: BrandResult | string): string {
  if (typeof option === 'string') {
    return option;
  }
  return option.name;
}

/**
 * Checks if two brand options are equal.
 */
function isOptionEqualToValue(
  option: BrandResult,
  value: BrandResult
): boolean {
  return option.domain === value.domain;
}

/**
 * Brand autocomplete component renderer.
 */
function BrandAutocompleteRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const {
    options,
    inputValue,
    onInputChange,
    onChange,
    name,
    label,
    placeholder,
    error,
    helperText,
    isSearching,
    disabled,
  } = props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
        InputProps={{
          ...parameters.InputProps,
          endAdornment: (
            <>
              {isSearching && <CircularProgress color="inherit" size={20} />}
              {parameters.InputProps.endAdornment}
            </>
          ),
        }}
      />
    );
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      size="small"
      options={options}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onChange={onChange}
      disabled={disabled}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(options) => options} // Server-side filtering
    />
  );
}

/**
 * An autocomplete component for searching and selecting brands.
 * Displays company logos with names and domains in the dropdown.
 *
 * Features:
 * - Free-form text entry (freeSolo mode)
 * - Server-side filtering via Logo.dev API
 * - Company logos displayed in dropdown options
 * - Loading indicator during search
 *
 * @example
 * ```tsx
 * const { search, results, isSearching } = useBrandSearch();
 *
 * <BrandAutocomplete
 *   options={results}
 *   inputValue={name}
 *   onInputChange={(_, value) => {
 *     setName(value);
 *     search(value);
 *   }}
 *   onChange={(_, value) => handleSelection(value)}
 *   isSearching={isSearching}
 *   label="Company name"
 * />
 * ```
 */
export const BrandAutocomplete = forwardRef(BrandAutocompleteRenderer);
